import React from "react";
import Box from "@mui/material/Box";
import { PageLoading } from "./loader";
import { reportClientError } from "../utils/clientErrors";

// Without a boundary, one error anywhere unmounts the whole app and leaves a blank page that
// only a refresh brings back. The most common error is a page's code that would not download
// (a dropped mobile connection): the page is reloaded once for that, before anyone sees a thing.

const RELOADED_AT_KEY = "chunkReloadAt";
const RELOAD_ONCE_WINDOW_MS = 60_000;

/** A failed download of a page's code (webpack's ChunkLoadError). */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  return e?.name === "ChunkLoadError" || /Loading (CSS )?chunk [\w-]+ failed/.test(e?.message ?? "");
}

function reloadedRecently(): boolean {
  try {
    return Date.now() - Number(sessionStorage.getItem(RELOADED_AT_KEY) ?? 0) < RELOAD_ONCE_WINDOW_MS;
  } catch {
    // No storage to remember a reload by: reloading could repeat forever, so never reload.
    return true;
  }
}

type Props = { children: React.ReactNode; /** A change here (the route) clears the error. */ resetKey?: string };
type State = { error: Error | null };

export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportClientError(error, info.componentStack ?? undefined);
    if (isChunkLoadError(error) && !reloadedRecently()) {
      try {
        sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
      } catch {
        // checked in reloadedRecently
      }
      window.location.reload();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    // The reload from componentDidCatch is on its way: show loading, not an error, until it lands.
    if (isChunkLoadError(error) && !reloadedRecently()) return <PageLoading />;
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 8, px: 2, textAlign: "center" }}>
        <Box sx={{ fontSize: 18, fontWeight: 700, color: "var(--ink)" }}>Coś poszło nie tak</Box>
        <Box sx={{ fontSize: 14, color: "var(--muted-strong)", maxWidth: 420 }}>
          {isChunkLoadError(error)
            ? "Nie udało się pobrać tej strony. Sprawdź połączenie i spróbuj ponownie."
            : "Ta strona nie mogła się wyświetlić. Odśwież ją, a jeśli to nie pomoże, wróć do wyszukiwania."}
        </Box>
        <Box
          component="button"
          onClick={() => window.location.reload()}
          sx={{
            px: 2.5, py: 1.25, borderRadius: "8px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
            bgcolor: "var(--accent)", border: "1px solid var(--accent)", color: "#fff",
          }}
        >
          Odśwież stronę
        </Box>
      </Box>
    );
  }
}
